import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const tabs = [
  { key: "overview", label: "Overview" },
  { key: "features", label: "Key Features" },
  { key: "benefits", label: "Benefits" },
];

const ProductSection = ({ data }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [currentImage, setCurrentImage] = useState(0);

  const navigate = useNavigate();

  const tabData = data?.tabs?.[activeTab];

  useEffect(() => {
    if (!data?.image?.length) return;

    const interval = setInterval(() => {
      setCurrentImage((prev) =>
        prev === data.image.length - 1 ? 0 : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [data]);

  useEffect(() => {
    setCurrentImage(0);
  }, [activeTab]);

  if (!data || !data.tabs) {
    return (
      <div className="text-center py-20 text-gray-500">
        No Data Found
      </div>
    );
  }

  return (
    <section className="relative py-14 sm:py-20 lg:py-24 bg-[#f8fafc] overflow-hidden">

      {/* TOP HEADING */}
      

      {/* FULL WIDTH TABS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">

        <div className="grid grid-cols-3 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">

          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative py-4 sm:py-5 text-sm sm:text-base lg:text-lg font-medium transition-all duration-300 ${
                activeTab === tab.key
                  ? "text-white"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              {activeTab === tab.key && (
                <motion.div
                  layoutId={`active-tab-${data.name}`}
                  className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600"
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                />
              )}

              <span className="relative z-10">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* TAB CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <AnimatePresence mode="wait">

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -25 }}
            transition={{ duration: 0.4 }}
          >

            {/* ================= OVERVIEW ================= */}
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

                {/* LEFT CONTENT */}
                <div>

                  <h2 className="text-2xl sm:text-3xl lg:text-4xl  text-[#0f172a] leading-snug">
                    {tabData.title}
                  </h2>

                  {tabData.Subtitles && (
                    <p className="mt-4 text-lg sm:text-xl text-blue-600 ">
                      {tabData.Subtitles}
                    </p>
                  )}

                  <p className="mt-6 text-base sm:text-lg text-gray-700 leading-8">
                    {tabData.description}
                  </p>

                  {tabData.description2 && (
                    <p className="mt-5 text-base sm:text-lg text-gray-700 leading-8">
                      {tabData.description2}
                    </p>
                  )}

                  <button
                    onClick={() => navigate("/demo")}
                    className="
                    mt-10
                    px-7 sm:px-9
                    py-3 sm:py-4
                    rounded-xl
                    bg-gradient-to-r from-blue-600 to-purple-600
                    text-white
                    text-sm sm:text-lg
                    font-medium
                    shadow-lg
                    hover:scale-105
                    transition-all
                    duration-300
                    "
                  >
                    Explore Solution
                  </button>
                </div>

                {/* RIGHT IMAGE */}
                <div className="relative flex justify-center">

                  <div className="relative w-full max-w-[650px]">

                    {/* Glow */}
                    <div className="absolute -inset-6 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl rounded-[40px]"></div>

                    {/* Card */}
                    <div className="relative bg-white border border-gray-200 rounded-[30px] shadow-2xl overflow-hidden p-5">

                      <div className="relative aspect-[4/3] flex items-center justify-center">

                        <AnimatePresence mode="wait">
                          <motion.img
                            key={currentImage}
                            src={data.image?.[currentImage]}
                            alt="product"
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.7 }}
                            className="w-full h-full object-contain"
                          />
                        </AnimatePresence>

                      </div>
                    </div>

                    {/* Dots */}
                    <div className="flex justify-center gap-3 mt-6">
                      {data.image?.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentImage(i)}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            i === currentImage
                              ? "bg-black scale-125"
                              : "bg-gray-400"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= FEATURES ================= */}
            {activeTab === "features" && (
              <div>

                

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

                  <table className="w-full">

                    <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">

                      <tr>
                        <th className="text-left px-6 py-5 text-lg  w-[30%]">
                          Feature
                        </th>
                        <th className="text-left px-6 py-5 text-lg font-semibold">
                          Client-friendly message
                        </th>

                        
                      </tr>
                    </thead>

                    <tbody>

                      {tabData.tableData?.map((item, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition"
                        >
                          <td className="px-6 py-5  text-[#0f172a]">
                            {item.feature}
                          </td>

                          <td className="px-6 py-5 text-gray-700 leading-7">
                            {item.details}
                          </td>
                        </tr>
                      ))}

                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ================= BENEFITS ================= */}
            {activeTab === "benefits" && (
              <div>
<p className=" text-base sm:text-2xl leading-8 mb-8 text-red-500">
                    {tabData.description}
                  </p>
                

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  

                  <table className="w-full">

                    <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">

                      <tr>
                        <th className="text-left px-6 py-5 text-lg  w-[30%]">
                          Expected impact   
                        </th>

                        <th className="text-left px-6 py-5 text-lg ">
                          What it means for the client
                        </th>
                      </tr>
                    </thead>

                    <tbody>

                      {tabData.tableData?.map((item, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition"
                        >
                          <td className="px-6 py-5  text-[#0f172a]">
                            {item.impact}
                          </td>

                          <td className="px-6 py-5 text-gray-700 leading-7">
                            {item.details}
                          </td>
                        </tr>
                      ))}

                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </motion.div>

        </AnimatePresence>
      </div>
    </section>
  );
};

export default ProductSection;